import { Mail, Phone, Calendar, MapPin, Heart, Sparkles, ArrowUpRight, Clock } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AcceptedInsurance from "@/components/AcceptedInsurance";
import ContactForm from "@/components/ContactForm";
import { Button } from "@/components/ui/button";
import { SITE } from "@/lib/site";

const CARDS = [
  {
    icon: Phone,
    label: "Call us",
    value: SITE.phone,
    sub: `${SITE.ext} · Fax ${SITE.fax}`,
    href: `tel:${SITE.phoneRaw}`,
    testId: "contact-card-phone",
  },
  {
    icon: Mail,
    label: "Email us",
    value: SITE.email,
    sub: "Reply within 1 business day",
    href: `mailto:${SITE.email}`,
    testId: "contact-card-email",
  },
  {
    icon: MapPin,
    label: "Visit us",
    value: "2259 W Taylor ST",
    sub: "Chicago, IL 60612",
    href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(SITE.address)}`,
    external: true,
    testId: "contact-card-address",
  },
  {
    icon: Calendar,
    label: "Flexible visits",
    value: "Telehealth & in-person",
    sub: `Serving all of ${SITE.state}`,
    testId: "contact-card-visits",
  },
];

export default function Contact() {
  return (
    <div className="min-h-screen">
      <Navigation />

      {/* HERO */}
      <section className="relative pt-28 md:pt-32 pb-10 md:pb-14">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="glow-orb top-10 -left-20 w-[420px] h-[420px] bg-primary/30" />
        <div className="glow-orb top-20 -right-20 w-[420px] h-[420px] bg-accent/20" />
        <div className="container mx-auto relative z-10 text-center max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs font-medium mb-6">
            <Heart className="w-3.5 h-3.5" /> We&rsquo;re here for you
          </div>
          <h1 data-testid="contact-title" className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-balance">
            Let&rsquo;s <em className="not-italic gradient-text font-light">talk.</em>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-xl mx-auto text-pretty">
            You don&rsquo;t have to do this alone. We&rsquo;re here to help.
          </p>
        </div>
      </section>

      <main className="pb-10 md:pb-14">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">

            {/* Form */}
            <ContactForm />

            {/* Contact cards */}
            <section className="mt-8 md:mt-12 mb-8 md:mb-12">
              <div className="text-center mb-10">
                <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-3">Reach us directly</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {CARDS.map((c) => {
                  const inner = (
                    <>
                      <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <c.icon className="w-5 h-5" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1">{c.label}</p>
                        <p className="font-display text-xl tracking-tight truncate group-hover:text-primary transition-colors">{c.value}</p>
                        <p className="text-sm text-muted-foreground mt-0.5">{c.sub}</p>
                      </div>
                      {c.href && (
                        <ArrowUpRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                      )}
                    </>
                  );

                  if (c.href) {
                    return (
                      <a
                        key={c.label}
                        data-testid={c.testId}
                        href={c.href}
                        target={c.external ? "_blank" : undefined}
                        rel={c.external ? "noopener noreferrer" : undefined}
                        className="group flex items-start gap-4 rounded-2xl border border-border bg-card/40 p-6 transition-all hover:border-primary/40 hover:bg-card"
                      >
                        {inner}
                      </a>
                    );
                  }
                  return (
                    <div
                      key={c.label}
                      data-testid={c.testId}
                      className="group flex items-start gap-4 rounded-2xl border border-border bg-card/40 p-6"
                    >
                      {inner}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* New patients welcome */}
            <section className="mb-8 md:mb-12 rounded-3xl border border-border bg-card/40 p-6 md:p-10 relative overflow-hidden">
              <div className="absolute top-5 right-5"><Sparkles className="w-6 h-6 text-primary/30" /></div>
              <div className="flex items-center gap-3 mb-4">
                <span className="grid place-items-center w-11 h-11 rounded-2xl bg-primary text-primary-foreground">
                  <Heart className="w-5 h-5" />
                </span>
                <h2 className="font-display text-3xl tracking-tight">New patients welcome</h2>
              </div>
              <p className="text-lg leading-relaxed text-muted-foreground mb-6 max-w-2xl">
                The first step is the hardest. We&rsquo;ll make the rest easy.
              </p>
              <Button
                size="lg"
                data-testid="contact-cta-portal"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12 px-7"
              >
                Schedule Appointment
              </Button>
            </section>

            {/* Insurance */}
            <section className="mb-8 md:mb-12 rounded-3xl border border-border bg-card/40 p-6 md:p-10">
              <AcceptedInsurance />
            </section>

            {/* Address & hours */}
            <section className="grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-border bg-card/40 p-7 flex items-start gap-4">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 text-primary shrink-0">
                  <MapPin className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1">Office</p>
                  <p className="font-display text-xl tracking-tight">{SITE.address}</p>
                  <p className="text-sm text-muted-foreground mt-1">In-person & telehealth visits available.</p>
                </div>
              </div>
              <div className="rounded-2xl border border-border bg-card/40 p-7 flex items-start gap-4">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 text-primary shrink-0">
                  <Clock className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1">Office hours</p>
                  <p className="font-display text-xl tracking-tight">{SITE.hours}</p>
                  <p className="text-sm text-muted-foreground mt-1">Closed weekends and federal holidays.</p>
                </div>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
